import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Building2,
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Eye,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  Settings,
  Users,
  FileText,
  Percent,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Globe,
  Loader2,
  RefreshCw,
  Shield,
  Landmark,
  Building,
  Star
} from "lucide-react";

const tiposOrgao = [
  { value: "FEDERAL", label: "Federal", color: "bg-blue-100 text-blue-800" },
  { value: "ESTADUAL", label: "Estadual", color: "bg-green-100 text-green-800" },
  { value: "MUNICIPAL", label: "Municipal", color: "bg-purple-100 text-purple-800" },
  { value: "INSS", label: "INSS", color: "bg-orange-100 text-orange-800" },
  { value: "BOLSA_FAMILIA", label: "Bolsa Família", color: "bg-pink-100 text-pink-800" },
  { value: "MILITAR", label: "Militar", color: "bg-red-100 text-red-800" },
  { value: "CLT_EMPRESA_PRIVADA", label: "CLT/Empresa Privada", color: "bg-cyan-100 text-cyan-800" }
];

const orgaosIniciais = [
  { id: "1", codigo: "037", nome: "INSS - PREVIDÊNCIA SOCIAL", tipo: "INSS", uf: "TODOS", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 5, convenio: "DATAPREV", clientes: 0 },
  { id: "2", codigo: "20001", nome: "MINISTÉRIO DA ECONOMIA", tipo: "FEDERAL", uf: "DF", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIAPE", clientes: 0 },
  { id: "3", codigo: "20002", nome: "MINISTÉRIO DA SAÚDE", tipo: "FEDERAL", uf: "DF", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIAPE", clientes: 0 },
  { id: "4", codigo: "20003", nome: "MINISTÉRIO DA EDUCAÇÃO", tipo: "FEDERAL", uf: "DF", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIAPE", clientes: 0 },
  { id: "5", codigo: "57202", nome: "INSS - GERÊNCIA EXECUTIVA", tipo: "INSS", uf: "TODOS", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 5, convenio: "DATAPREV", clientes: 0 },
  { id: "6", codigo: "30001", nome: "GOVERNO DO ESTADO DE SÃO PAULO", tipo: "ESTADUAL", uf: "SP", ativo: true, margem_consignavel: 30, margem_cartao: 5, margem_beneficio: 0, convenio: "SPPrev", clientes: 0 },
  { id: "7", codigo: "30002", nome: "GOVERNO DO ESTADO DO RIO DE JANEIRO", tipo: "ESTADUAL", uf: "RJ", ativo: true, margem_consignavel: 30, margem_cartao: 5, margem_beneficio: 0, convenio: "RioPrevidência", clientes: 0 },
  { id: "8", codigo: "30003", nome: "GOVERNO DO ESTADO DE MINAS GERAIS", tipo: "ESTADUAL", uf: "MG", ativo: true, margem_consignavel: 30, margem_cartao: 5, margem_beneficio: 0, convenio: "IPSEMG", clientes: 0 },
  { id: "9", codigo: "40001", nome: "EXÉRCITO BRASILEIRO", tipo: "MILITAR", uf: "TODOS", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "EBConsig", clientes: 0 },
  { id: "10", codigo: "40002", nome: "MARINHA DO BRASIL", tipo: "MILITAR", uf: "TODOS", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "PAPEM", clientes: 0 },
  { id: "11", codigo: "40003", nome: "FORÇA AÉREA BRASILEIRA", tipo: "MILITAR", uf: "TODOS", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIGPES", clientes: 0 },
  { id: "12", codigo: "50001", nome: "PREFEITURA DE SÃO PAULO", tipo: "MUNICIPAL", uf: "SP", ativo: true, margem_consignavel: 30, margem_cartao: 5, margem_beneficio: 0, convenio: "SAMPAPREV", clientes: 0 },
  { id: "13", codigo: "50002", nome: "PREFEITURA DO RIO DE JANEIRO", tipo: "MUNICIPAL", uf: "RJ", ativo: true, margem_consignavel: 30, margem_cartao: 5, margem_beneficio: 0, convenio: "PREVI-RIO", clientes: 0 },
  { id: "14", codigo: "60001", nome: "BANCO CENTRAL DO BRASIL", tipo: "CLT_EMPRESA_PRIVADA", uf: "DF", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIAPE", clientes: 0 },
  { id: "15", codigo: "60002", nome: "IBGE", tipo: "CLT_EMPRESA_PRIVADA", uf: "RJ", ativo: true, margem_consignavel: 35, margem_cartao: 5, margem_beneficio: 0, convenio: "SIAPE", clientes: 0 }
];

export default function Orgaos() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [filtroUF, setFiltroUF] = useState("todos");
  const [filtroStatus, setFiltroStatus] = useState("todos");
  
  // Data states
  const [orgaos, setOrgaos] = useState([]);
  const [orgaoSelecionado, setOrgaoSelecionado] = useState(null);
  
  // Modal states
  const [showNovoOrgao, setShowNovoOrgao] = useState(false);
  const [showDetalhes, setShowDetalhes] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  
  // Lotação/Espécie management states
  const [showNovaLotacao, setShowNovaLotacao] = useState(false);
  const [showNovaEspecie, setShowNovaEspecie] = useState(false);
  const [lotacaoEditando, setLotacaoEditando] = useState(null);
  const [especieEditando, setEspecieEditando] = useState(null);
  const [novaLotacao, setNovaLotacao] = useState({ codigo: "", nome: "", ativo: true });
  const [novaEspecie, setNovaEspecie] = useState({ codigo: "", nome: "", tipo: "APOSENTADORIA", ativo: true });
  
  // Form state
  const [novoOrgao, setNovoOrgao] = useState({
    codigo: "",
    nome: "",
    tipo: "",
    uf: "",
    ativo: true,
    margem_consignavel: 35,
    margem_cartao: 5,
    margem_beneficio: 0,
    convenio: "",
    observacoes: "",
    contato_nome: "",
    contato_telefone: "",
    contato_email: "",
    website: ""
  });

  useEffect(() => {
    carregarOrgaos();
  }, []);

  const carregarOrgaos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orgaos`);
      if (response.data && response.data.length > 0) {
        setOrgaos(response.data);
      } else {
        // Inicializar com dados padrão
        await axios.post(`${API}/orgaos/inicializar`);
        const response2 = await axios.get(`${API}/orgaos`);
        setOrgaos(response2.data || orgaosIniciais);
      }
    } catch (error) {
      console.error("Erro ao carregar órgãos:", error);
      setOrgaos(orgaosIniciais);
    } finally {
      setLoading(false);
    }
  };

  const salvarOrgao = async () => {
    if (!novoOrgao.nome || !novoOrgao.tipo) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    setSaving(true);
    try {
      if (orgaoSelecionado) {
        await axios.put(`${API}/orgaos/${orgaoSelecionado.id}`, novoOrgao);
        toast.success("Órgão atualizado com sucesso!");
      } else {
        await axios.post(`${API}/orgaos`, novoOrgao);
        toast.success("Órgão cadastrado com sucesso!");
      }
      setShowNovoOrgao(false);
      resetForm();
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao salvar órgão");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const excluirOrgao = async () => {
    if (!orgaoSelecionado) return;
    
    try {
      await axios.delete(`${API}/orgaos/${orgaoSelecionado.id}`);
      toast.success("Órgão excluído com sucesso!");
      setShowDeleteConfirm(false);
      setOrgaoSelecionado(null);
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao excluir órgão");
      console.error(error);
    }
  };

  // ===== CRUD Lotações =====
  const salvarLotacao = async () => {
    if (!novaLotacao.codigo || !novaLotacao.nome) {
      toast.error("Preencha código e nome da lotação");
      return;
    }
    
    setSaving(true);
    try {
      if (lotacaoEditando) {
        // Atualizar lotação existente
        const lotacoesAtualizadas = orgaoSelecionado.lotacoes.map(l => 
          l.id === lotacaoEditando.id ? { ...l, ...novaLotacao } : l
        );
        await axios.put(`${API}/orgaos/${orgaoSelecionado.id}`, {
          ...orgaoSelecionado,
          lotacoes: lotacoesAtualizadas
        });
        toast.success("Lotação atualizada!");
      } else {
        // Nova lotação
        await axios.post(`${API}/orgaos/${orgaoSelecionado.id}/lotacoes`, novaLotacao);
        toast.success("Lotação adicionada!");
      }
      
      // Recarregar órgão
      const res = await axios.get(`${API}/orgaos/${orgaoSelecionado.id}`);
      setOrgaoSelecionado(res.data);
      setShowNovaLotacao(false);
      setLotacaoEditando(null);
      setNovaLotacao({ codigo: "", nome: "", ativo: true });
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao salvar lotação");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const excluirLotacao = async (lotacaoId) => {
    try {
      await axios.delete(`${API}/orgaos/${orgaoSelecionado.id}/lotacoes/${lotacaoId}`);
      toast.success("Lotação excluída!");
      
      // Recarregar órgão
      const res = await axios.get(`${API}/orgaos/${orgaoSelecionado.id}`);
      setOrgaoSelecionado(res.data);
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao excluir lotação");
      console.error(error);
    }
  };

  const editarLotacao = (lotacao) => {
    setLotacaoEditando(lotacao);
    setNovaLotacao({ codigo: lotacao.codigo, nome: lotacao.nome, ativo: lotacao.ativo !== false });
    setShowNovaLotacao(true);
  };

  // ===== CRUD Espécies =====
  const salvarEspecie = async () => {
    if (!novaEspecie.codigo || !novaEspecie.nome) {
      toast.error("Preencha código e nome da espécie");
      return;
    }
    
    setSaving(true);
    try {
      if (especieEditando) {
        // Atualizar espécie existente
        const especiesAtualizadas = orgaoSelecionado.especies.map(e => 
          e.id === especieEditando.id ? { ...e, ...novaEspecie } : e
        );
        await axios.put(`${API}/orgaos/${orgaoSelecionado.id}`, {
          ...orgaoSelecionado,
          especies: especiesAtualizadas
        });
        toast.success("Espécie atualizada!");
      } else {
        // Nova espécie
        await axios.post(`${API}/orgaos/${orgaoSelecionado.id}/especies`, novaEspecie);
        toast.success("Espécie adicionada!");
      }
      
      // Recarregar órgão
      const res = await axios.get(`${API}/orgaos/${orgaoSelecionado.id}`);
      setOrgaoSelecionado(res.data);
      setShowNovaEspecie(false);
      setEspecieEditando(null);
      setNovaEspecie({ codigo: "", nome: "", tipo: "APOSENTADORIA", ativo: true });
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao salvar espécie");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const excluirEspecie = async (especieId) => {
    try {
      await axios.delete(`${API}/orgaos/${orgaoSelecionado.id}/especies/${especieId}`);
      toast.success("Espécie excluída!");
      
      // Recarregar órgão
      const res = await axios.get(`${API}/orgaos/${orgaoSelecionado.id}`);
      setOrgaoSelecionado(res.data);
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao excluir espécie");
      console.error(error);
    }
  };

  const editarEspecie = (especie) => {
    setEspecieEditando(especie);
    setNovaEspecie({ codigo: especie.codigo, nome: especie.nome, tipo: especie.tipo, ativo: especie.ativo !== false });
    setShowNovaEspecie(true);
  };

  const toggleStatus = async (orgao) => {
    try {
      await axios.put(`${API}/orgaos/${orgao.id}`, { ...orgao, ativo: !orgao.ativo });
      toast.success(`Órgão ${orgao.ativo ? 'desativado' : 'ativado'} com sucesso!`);
      carregarOrgaos();
    } catch (error) {
      toast.error("Erro ao alterar status");
      console.error(error);
    }
  };

  const resetForm = () => {
    setNovoOrgao({
      codigo: "",
      nome: "",
      tipo: "",
      uf: "",
      ativo: true,
      margem_consignavel: 35,
      margem_cartao: 5,
      margem_beneficio: 0,
      convenio: "",
      observacoes: "",
      contato_nome: "",
      contato_telefone: "",
      contato_email: "",
      website: ""
    });
    setOrgaoSelecionado(null);
  };

  // ===== Importação de Órgãos =====
  const handleImportFile = async () => {
    if (!importFile) {
      toast.error("Selecione um arquivo para importar");
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await axios.post(`${API}/orgaos/importar`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success(`${response.data.importados || 0} órgãos importados com sucesso!`);
      setShowImportModal(false);
      setImportFile(null);
      carregarOrgaos();
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao importar órgãos";
      toast.error(msg);
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const editarOrgao = (orgao) => {
    setOrgaoSelecionado(orgao);
    setNovoOrgao({
      codigo: orgao.codigo || "",
      nome: orgao.nome || "",
      tipo: orgao.tipo || "",
      uf: orgao.uf || "",
      ativo: orgao.ativo ?? true,
      margem_consignavel: orgao.margem_consignavel || 35,
      margem_cartao: orgao.margem_cartao || 5,
      margem_beneficio: orgao.margem_beneficio || 0,
      convenio: orgao.convenio || "",
      observacoes: orgao.observacoes || "",
      contato_nome: orgao.contato_nome || "",
      contato_telefone: orgao.contato_telefone || "",
      contato_email: orgao.contato_email || "",
      website: orgao.website || ""
    });
    setShowNovoOrgao(true);
  };

  const getTipoBadge = (tipo) => {
    const config = tiposOrgao.find(t => t.value === tipo);
    if (!config) return <Badge variant="outline">{tipo}</Badge>;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const filtrarOrgaos = () => {
    let resultado = orgaos;
    
    if (searchTerm) {
      resultado = resultado.filter(o => 
        o.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.codigo?.includes(searchTerm)
      );
    }
    
    if (filtroTipo !== "todos") {
      resultado = resultado.filter(o => o.tipo === filtroTipo);
    }
    
    if (filtroUF !== "todos") {
      resultado = resultado.filter(o => o.uf === filtroUF || o.uf === "TODOS");
    }
    
    if (filtroStatus !== "todos") {
      resultado = resultado.filter(o => filtroStatus === "ativo" ? o.ativo : !o.ativo);
    }
    
    return resultado;
  };

  const estados = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG",
    "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO", "TODOS"
  ];

  // Estatísticas
  const stats = {
    total: orgaos.length,
    ativos: orgaos.filter(o => o.ativo).length,
    inativos: orgaos.filter(o => !o.ativo).length,
    porTipo: tiposOrgao.map(t => ({
      ...t,
      count: orgaos.filter(o => o.tipo === t.value).length
    }))
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Órgãos</h1>
          <p className="text-gray-500 mt-1">Cadastro padrão de órgãos públicos e convênios (compartilhado por todas as empresas)</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" onClick={carregarOrgaos}>
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setShowImportModal(true)}>
            <Upload className="h-4 w-4" />
            Importar
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Exportar
          </Button>
          <Dialog open={showNovoOrgao} onOpenChange={(open) => { setShowNovoOrgao(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4" />
                Novo Órgão
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{orgaoSelecionado ? "Editar Órgão" : "Novo Órgão"}</DialogTitle>
                <DialogDescription>Cadastre ou edite os dados do órgão público</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Dados Básicos */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Dados Básicos
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Código</Label>
                      <Input
                        value={novoOrgao.codigo}
                        onChange={(e) => setNovoOrgao({...novoOrgao, codigo: e.target.value})}
                        placeholder="Ex: 037, 20001"
                      />
                    </div>
                    <div>
                      <Label>Tipo *</Label>
                      <Select value={novoOrgao.tipo} onValueChange={(v) => setNovoOrgao({...novoOrgao, tipo: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          {tiposOrgao.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label>Nome do Órgão *</Label>
                      <Input
                        value={novoOrgao.nome}
                        onChange={(e) => setNovoOrgao({...novoOrgao, nome: e.target.value})}
                        placeholder="Nome completo do órgão"
                      />
                    </div>
                    <div>
                      <Label>UF</Label>
                      <Select value={novoOrgao.uf} onValueChange={(v) => setNovoOrgao({...novoOrgao, uf: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {estados.map((uf) => (
                            <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Convênio/Sistema</Label>
                      <Input
                        value={novoOrgao.convenio}
                        onChange={(e) => setNovoOrgao({...novoOrgao, convenio: e.target.value})}
                        placeholder="Ex: SIAPE, DATAPREV"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Margens */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                    <Percent className="h-4 w-4" />
                    Margens de Consignação
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Margem Consignável (%)</Label>
                      <Input
                        type="number"
                        value={novoOrgao.margem_consignavel}
                        onChange={(e) => setNovoOrgao({...novoOrgao, margem_consignavel: parseFloat(e.target.value) || 0})}
                        placeholder="35"
                      />
                    </div>
                    <div>
                      <Label>Margem Cartão (%)</Label>
                      <Input
                        type="number"
                        value={novoOrgao.margem_cartao}
                        onChange={(e) => setNovoOrgao({...novoOrgao, margem_cartao: parseFloat(e.target.value) || 0})}
                        placeholder="5"
                      />
                    </div>
                    <div>
                      <Label>Margem Benefício (%)</Label>
                      <Input
                        type="number"
                        value={novoOrgao.margem_beneficio}
                        onChange={(e) => setNovoOrgao({...novoOrgao, margem_beneficio: parseFloat(e.target.value) || 0})}
                        placeholder="5"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Contato */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-gray-700 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Contato
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome do Contato</Label>
                      <Input
                        value={novoOrgao.contato_nome}
                        onChange={(e) => setNovoOrgao({...novoOrgao, contato_nome: e.target.value})}
                        placeholder="Nome do responsável"
                      />
                    </div>
                    <div>
                      <Label>Telefone</Label>
                      <Input
                        value={novoOrgao.contato_telefone}
                        onChange={(e) => setNovoOrgao({...novoOrgao, contato_telefone: e.target.value})}
                        placeholder="(00) 00000-0000"
                      />
                    </div>
                    <div>
                      <Label>E-mail</Label>
                      <Input
                        type="email"
                        value={novoOrgao.contato_email}
                        onChange={(e) => setNovoOrgao({...novoOrgao, contato_email: e.target.value})}
                        placeholder="email@orgao.gov.br"
                      />
                    </div>
                    <div>
                      <Label>Website</Label>
                      <Input
                        value={novoOrgao.website}
                        onChange={(e) => setNovoOrgao({...novoOrgao, website: e.target.value})}
                        placeholder="https://www.orgao.gov.br"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Observações e Status */}
                <div className="space-y-4">
                  <div>
                    <Label>Observações</Label>
                    <Textarea
                      value={novoOrgao.observacoes}
                      onChange={(e) => setNovoOrgao({...novoOrgao, observacoes: e.target.value})}
                      placeholder="Informações adicionais sobre o órgão..."
                      rows={3}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={novoOrgao.ativo}
                      onCheckedChange={(v) => setNovoOrgao({...novoOrgao, ativo: v})}
                    />
                    <Label>Órgão Ativo</Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowNovoOrgao(false); resetForm(); }}>Cancelar</Button>
                <Button onClick={salvarOrgao} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {orgaoSelecionado ? "Salvar Alterações" : "Cadastrar Órgão"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total de Órgãos</CardTitle>
            <Building2 className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-gray-500 mt-1">Cadastrados no sistema</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Ativos</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.ativos}</div>
            <p className="text-xs text-gray-500 mt-1">Disponíveis para operação</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Inativos</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{stats.inativos}</div>
            <p className="text-xs text-gray-500 mt-1">Temporariamente suspensos</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Clientes Vinculados</CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 px-2 text-xs"
              onClick={async () => {
                try {
                  await axios.post(`${API}/orgaos/recalcular-clientes`);
                  toast.success("Contagem de clientes atualizada!");
                  carregarOrgaos();
                } catch (error) {
                  toast.error("Erro ao recalcular");
                }
              }}
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Atualizar
            </Button>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{orgaos.reduce((acc, o) => acc + (o.clientes || 0), 0).toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">Total de beneficiários</p>
          </CardContent>
        </Card>
      </div>

      {/* Cards por Tipo */}
      <div className="grid gap-3 md:grid-cols-6">
        {stats.porTipo.map((tipo) => (
          <Card 
            key={tipo.value} 
            className={`cursor-pointer hover:shadow-md transition-shadow ${filtroTipo === tipo.value ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setFiltroTipo(filtroTipo === tipo.value ? "todos" : tipo.value)}
          >
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className={tipo.color}>{tipo.label}</Badge>
                  <p className="text-2xl font-bold mt-2">{tipo.count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Card de Instituição Principal (quando filtro Federal) */}
      {filtroTipo === "FEDERAL" && (
        <Card className="border-l-4 border-l-green-500 bg-green-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-full">
                <Shield className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">1ª Instituição</CardTitle>
                <CardDescription>Instituição principal vinculada aos servidores</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <Badge className="bg-green-600 text-white text-lg px-3 py-1">131</Badge>
              <span className="font-semibold text-lg">SERVIDOR FEDERAL CIVIL</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card de Resumo por UF (quando filtro Estadual) */}
      {filtroTipo === "ESTADUAL" && (
        <Card className="border-l-4 border-l-blue-500 bg-blue-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-full">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Governos Estaduais por UF</CardTitle>
                <CardDescription>Distribuição de órgãos estaduais por estado</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2">
              {(() => {
                // Agrupar órgãos estaduais por UF
                const estaduaisPorUF = orgaos
                  .filter(o => o.tipo === "ESTADUAL")
                  .reduce((acc, orgao) => {
                    const uf = orgao.uf || "N/D";
                    if (!acc[uf]) {
                      acc[uf] = { total: 0, ativos: 0, inativos: 0, orgaos: [] };
                    }
                    acc[uf].total++;
                    if (orgao.ativo) {
                      acc[uf].ativos++;
                    } else {
                      acc[uf].inativos++;
                    }
                    acc[uf].orgaos.push(orgao.nome);
                    return acc;
                  }, {});
                
                // Ordenar por UF alfabeticamente
                const ufsOrdenadas = Object.entries(estaduaisPorUF)
                  .sort((a, b) => a[0].localeCompare(b[0]));
                
                if (ufsOrdenadas.length === 0) {
                  return (
                    <div className="col-span-full text-center text-gray-500 py-4">
                      Nenhum órgão estadual cadastrado
                    </div>
                  );
                }
                
                return ufsOrdenadas.map(([uf, data]) => (
                  <div 
                    key={uf} 
                    className={`bg-white rounded-lg border p-2 cursor-pointer hover:bg-blue-100 transition-colors ${filtroUF === uf ? 'ring-2 ring-blue-500 bg-blue-50' : ''}`}
                    onClick={() => setFiltroUF(filtroUF === uf ? 'todos' : uf)}
                    title={data.orgaos.join('\n')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge className="bg-blue-600 text-white font-bold text-xs">{uf}</Badge>
                      <span className="text-lg font-bold text-blue-700">{data.total}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-600 font-medium">{data.ativos} ✓</span>
                      <span className="text-red-500 font-medium">{data.inativos} ✗</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="text-green-600">✓</span> Ativas</span>
              <span className="flex items-center gap-1"><span className="text-red-500">✗</span> Inativas</span>
              <span>| Clique em uma UF para filtrar</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card de Instituição Principal (quando filtro CLT/Empresa Privada) */}
      {filtroTipo === "CLT_EMPRESA_PRIVADA" && (
        <Card className="border-l-4 border-l-cyan-500 bg-cyan-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-full">
                <Building className="h-5 w-5 text-cyan-600" />
              </div>
              <div>
                <CardTitle className="text-lg">1ª Instituição</CardTitle>
                <CardDescription>Instituição padrão para trabalhadores CLT</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <Badge className="bg-cyan-600 text-white text-lg px-3 py-1">006</Badge>
              <span className="font-semibold text-lg">CLT_EMPRESA_PRIVADA</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card de Resumo por UF (quando filtro Municipal) */}
      {filtroTipo === "MUNICIPAL" && (
        <Card className="border-l-4 border-l-purple-500 bg-purple-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-full">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Prefeituras por UF</CardTitle>
                <CardDescription>Distribuição de órgãos municipais por estado</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-9 gap-2">
              {(() => {
                // Agrupar órgãos municipais por UF
                const municipaisPorUF = orgaos
                  .filter(o => o.tipo === "MUNICIPAL")
                  .reduce((acc, orgao) => {
                    const uf = orgao.uf || "N/D";
                    if (!acc[uf]) {
                      acc[uf] = { total: 0, ativos: 0, inativos: 0, orgaos: [] };
                    }
                    acc[uf].total++;
                    if (orgao.ativo) {
                      acc[uf].ativos++;
                    } else {
                      acc[uf].inativos++;
                    }
                    acc[uf].orgaos.push(orgao.nome);
                    return acc;
                  }, {});
                
                // Ordenar por UF alfabeticamente
                const ufsOrdenadas = Object.entries(municipaisPorUF)
                  .sort((a, b) => a[0].localeCompare(b[0]));
                
                if (ufsOrdenadas.length === 0) {
                  return (
                    <div className="col-span-full text-center text-gray-500 py-4">
                      Nenhuma prefeitura cadastrada
                    </div>
                  );
                }
                
                return ufsOrdenadas.map(([uf, data]) => (
                  <div 
                    key={uf} 
                    className={`bg-white rounded-lg border p-2 cursor-pointer hover:bg-purple-100 transition-colors ${filtroUF === uf ? 'ring-2 ring-purple-500 bg-purple-50' : ''}`}
                    onClick={() => setFiltroUF(filtroUF === uf ? 'todos' : uf)}
                    title={data.orgaos.join('\n')}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <Badge className="bg-purple-600 text-white font-bold text-xs">{uf}</Badge>
                      <span className="text-lg font-bold text-purple-700">{data.total}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-green-600 font-medium">{data.ativos} ✓</span>
                      <span className="text-red-500 font-medium">{data.inativos} ✗</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
            <div className="flex items-center justify-center gap-4 mt-3 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="text-green-600">✓</span> Ativas</span>
              <span className="flex items-center gap-1"><span className="text-red-500">✗</span> Inativas</span>
              <span>| Clique em uma UF para filtrar</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros e Tabela */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>
              {filtroTipo === "FEDERAL" 
                ? "Lista de Órgãos e Lotação" 
                : filtroTipo === "CLT_EMPRESA_PRIVADA"
                  ? "Empregador"
                  : filtroTipo === "INSS" 
                    ? "Lista de Órgãos e Espécies"
                    : "Lista de Órgãos"}
            </CardTitle>
            <CardDescription>
              {filtroTipo === "CLT_EMPRESA_PRIVADA" 
                ? "Gerencie os empregadores cadastrados" 
                : "Gerencie os órgãos públicos cadastrados"}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Tipos</SelectItem>
                {tiposOrgao.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroUF} onValueChange={setFiltroUF}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="UF" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todas UFs</SelectItem>
                {estados.map((uf) => (
                  <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="ativo">Ativos</SelectItem>
                <SelectItem value="inativo">Inativos</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar órgão..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-[250px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>UF</TableHead>
                <TableHead>Convênio</TableHead>
                <TableHead className="text-center">Margem</TableHead>
                <TableHead className="text-center">Clientes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                    <p className="text-gray-500 mt-2">Carregando órgãos...</p>
                  </TableCell>
                </TableRow>
              ) : filtrarOrgaos().length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    Nenhum órgão encontrado
                  </TableCell>
                </TableRow>
              ) : (
                filtrarOrgaos().map((orgao) => (
                  <TableRow key={orgao.id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-sm">{orgao.codigo}</TableCell>
                    <TableCell>
                      <p className="font-medium">{orgao.nome}</p>
                    </TableCell>
                    <TableCell>{getTipoBadge(orgao.tipo)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{orgao.uf}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{orgao.convenio || "-"}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm font-medium">{orgao.margem_consignavel}%</span>
                      <span className="text-gray-400 text-xs"> + {orgao.margem_cartao}%</span>
                    </TableCell>
                    <TableCell className="text-center font-semibold">{orgao.clientes?.toLocaleString() || 0}</TableCell>
                    <TableCell>
                      <Badge className={orgao.ativo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                        {orgao.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0"
                          onClick={() => { setOrgaoSelecionado(orgao); setShowDetalhes(true); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0"
                          onClick={() => editarOrgao(orgao)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          onClick={() => { setOrgaoSelecionado(orgao); setShowDeleteConfirm(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Card de Espécies (quando filtro INSS) */}
      {filtroTipo === "INSS" && (
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-600" />
                Espécies de Benefício - INSS
              </CardTitle>
              <CardDescription>Lista de todas as espécies cadastradas para órgãos INSS</CardDescription>
            </div>
            <Button 
              size="sm" 
              className="gap-1 bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                const inssOrgao = orgaos.find(o => o.tipo === "INSS");
                if (inssOrgao) {
                  setOrgaoSelecionado(inssOrgao);
                  setNovaEspecie({ codigo: "", nome: "", tipo: "APOSENTADORIA", ativo: true });
                  setEspecieEditando(null);
                  setShowNovaEspecie(true);
                }
              }}
            >
              <Plus className="h-4 w-4" />
              Nova Espécie
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nome da Espécie</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Órgão</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgaos.filter(o => o.tipo === "INSS").flatMap(orgao => 
                  (orgao.especies || []).map(esp => ({ ...esp, orgao }))
                ).length > 0 ? (
                  orgaos.filter(o => o.tipo === "INSS").flatMap(orgao => 
                    (orgao.especies || []).map(esp => ({ ...esp, orgao }))
                  ).map((esp) => (
                    <TableRow key={esp.id}>
                      <TableCell className="font-mono text-sm font-bold">{esp.codigo}</TableCell>
                      <TableCell className="font-medium">{esp.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={
                          esp.tipo === "APOSENTADORIA" ? "bg-blue-50 text-blue-700" :
                          esp.tipo === "PENSAO" ? "bg-purple-50 text-purple-700" :
                          "bg-orange-50 text-orange-700"
                        }>
                          {esp.tipo}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">{esp.orgao?.nome?.slice(0, 25)}...</TableCell>
                      <TableCell>
                        <Badge className={esp.ativo !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {esp.ativo !== false ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0" 
                            onClick={() => { setOrgaoSelecionado(esp.orgao); editarEspecie(esp); }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700" 
                            onClick={() => { setOrgaoSelecionado(esp.orgao); excluirEspecie(esp.id); }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      Nenhuma espécie cadastrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog de Detalhes */}
      <Dialog open={showDetalhes} onOpenChange={setShowDetalhes}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Órgão</DialogTitle>
            <DialogDescription>{orgaoSelecionado?.codigo}</DialogDescription>
          </DialogHeader>
          {orgaoSelecionado && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{orgaoSelecionado.nome}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    {getTipoBadge(orgaoSelecionado.tipo)}
                    <Badge variant="outline">{orgaoSelecionado.uf}</Badge>
                    <Badge className={orgaoSelecionado.ativo ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                      {orgaoSelecionado.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <Tabs defaultValue="info" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Informações</TabsTrigger>
                  <TabsTrigger value="lotacoes" disabled={orgaoSelecionado.tipo === "INSS"}>
                    Lotações ({orgaoSelecionado.lotacoes?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="especies" disabled={orgaoSelecionado.tipo !== "INSS"}>
                    Espécies ({orgaoSelecionado.especies?.length || 0})
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-500">Convênio</Label>
                      <p className="font-medium">{orgaoSelecionado.convenio || "-"}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Clientes</Label>
                      <p className="font-medium">{orgaoSelecionado.clientes?.toLocaleString() || 0}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Percent className="h-4 w-4" />
                      Margens de Consignação
                    </h4>
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-blue-600">{orgaoSelecionado.margem_consignavel}%</p>
                        <p className="text-xs text-gray-500">Consignável</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600">{orgaoSelecionado.margem_cartao}%</p>
                        <p className="text-xs text-gray-500">Cartão</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{orgaoSelecionado.margem_beneficio}%</p>
                        <p className="text-xs text-gray-500">Benefício</p>
                      </div>
                    </div>
                  </div>
                  
                  {(orgaoSelecionado.contato_nome || orgaoSelecionado.contato_telefone || orgaoSelecionado.contato_email) && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Contato</h4>
                      {orgaoSelecionado.contato_nome && <p className="text-sm"><strong>Nome:</strong> {orgaoSelecionado.contato_nome}</p>}
                      {orgaoSelecionado.contato_telefone && <p className="text-sm"><strong>Telefone:</strong> {orgaoSelecionado.contato_telefone}</p>}
                      {orgaoSelecionado.contato_email && <p className="text-sm"><strong>E-mail:</strong> {orgaoSelecionado.contato_email}</p>}
                      {orgaoSelecionado.website && <p className="text-sm"><strong>Website:</strong> {orgaoSelecionado.website}</p>}
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="lotacoes" className="mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Lotações Vinculadas</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{orgaoSelecionado.lotacoes?.length || 0} lotações</Badge>
                        <Button 
                          size="sm" 
                          className="gap-1 bg-blue-600 hover:bg-blue-700"
                          onClick={() => { setNovaLotacao({ codigo: "", nome: "", ativo: true }); setLotacaoEditando(null); setShowNovaLotacao(true); }}
                        >
                          <Plus className="h-3 w-3" />
                          Nova Lotação
                        </Button>
                      </div>
                    </div>
                    <ScrollArea className="h-[300px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Código</TableHead>
                            <TableHead>Nome da Lotação</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orgaoSelecionado.lotacoes?.length > 0 ? (
                            orgaoSelecionado.lotacoes.map((lot) => (
                              <TableRow key={lot.id}>
                                <TableCell className="font-mono text-sm">{lot.codigo}</TableCell>
                                <TableCell>{lot.nome}</TableCell>
                                <TableCell>
                                  <Badge className={lot.ativo !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                    {lot.ativo !== false ? "Ativo" : "Inativo"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => editarLotacao(lot)}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => excluirLotacao(lot.id)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                                Nenhuma lotação cadastrada
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </TabsContent>
                
                <TabsContent value="especies" className="mt-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">Espécies de Benefício</h4>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{orgaoSelecionado.especies?.length || 0} espécies</Badge>
                        <Button 
                          size="sm" 
                          className="gap-1 bg-orange-600 hover:bg-orange-700"
                          onClick={() => { setNovaEspecie({ codigo: "", nome: "", tipo: "APOSENTADORIA", ativo: true }); setEspecieEditando(null); setShowNovaEspecie(true); }}
                        >
                          <Plus className="h-3 w-3" />
                          Nova Espécie
                        </Button>
                      </div>
                    </div>
                    <ScrollArea className="h-[300px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Código</TableHead>
                            <TableHead>Nome da Espécie</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">Ações</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {orgaoSelecionado.especies?.length > 0 ? (
                            orgaoSelecionado.especies.map((esp) => (
                              <TableRow key={esp.id}>
                                <TableCell className="font-mono text-sm">{esp.codigo}</TableCell>
                                <TableCell>{esp.nome}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={
                                    esp.tipo === "APOSENTADORIA" ? "bg-blue-50 text-blue-700" :
                                    esp.tipo === "PENSAO" ? "bg-purple-50 text-purple-700" :
                                    "bg-orange-50 text-orange-700"
                                  }>
                                    {esp.tipo}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className={esp.ativo !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                                    {esp.ativo !== false ? "Ativo" : "Inativo"}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => editarEspecie(esp)}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => excluirEspecie(esp.id)}>
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                Nenhuma espécie cadastrada
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>
              
              <Separator />
              
              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => { setShowDetalhes(false); editarOrgao(orgaoSelecionado); }}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button variant="outline" onClick={() => toggleStatus(orgaoSelecionado)}>
                  {orgaoSelecionado.ativo ? "Desativar" : "Ativar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o órgão "{orgaoSelecionado?.nome}"?
              Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={excluirOrgao}>
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nova/Editar Lotação */}
      <Dialog open={showNovaLotacao} onOpenChange={(open) => { setShowNovaLotacao(open); if (!open) { setLotacaoEditando(null); setNovaLotacao({ codigo: "", nome: "", ativo: true }); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{lotacaoEditando ? "Editar Lotação" : "Nova Lotação"}</DialogTitle>
            <DialogDescription>
              {lotacaoEditando ? "Edite os dados da lotação" : "Adicione uma nova lotação ao órgão"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={novaLotacao.codigo}
                  onChange={(e) => setNovaLotacao({...novaLotacao, codigo: e.target.value})}
                  placeholder="Ex: 20101"
                />
              </div>
              <div className="col-span-2">
                <Label>Nome da Lotação *</Label>
                <Input
                  value={novaLotacao.nome}
                  onChange={(e) => setNovaLotacao({...novaLotacao, nome: e.target.value})}
                  placeholder="Ex: GABINETE DA PRESIDÊNCIA"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={novaLotacao.ativo}
                onCheckedChange={(v) => setNovaLotacao({...novaLotacao, ativo: v})}
              />
              <Label>Lotação Ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNovaLotacao(false); setLotacaoEditando(null); }}>Cancelar</Button>
            <Button onClick={salvarLotacao} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {lotacaoEditando ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nova/Editar Espécie */}
      <Dialog open={showNovaEspecie} onOpenChange={(open) => { setShowNovaEspecie(open); if (!open) { setEspecieEditando(null); setNovaEspecie({ codigo: "", nome: "", tipo: "APOSENTADORIA", ativo: true }); }}}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{especieEditando ? "Editar Espécie" : "Nova Espécie"}</DialogTitle>
            <DialogDescription>
              {especieEditando ? "Edite os dados da espécie de benefício" : "Adicione uma nova espécie de benefício"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Código *</Label>
                <Input
                  value={novaEspecie.codigo}
                  onChange={(e) => setNovaEspecie({...novaEspecie, codigo: e.target.value})}
                  placeholder="Ex: 41"
                />
              </div>
              <div className="col-span-2">
                <Label>Nome da Espécie *</Label>
                <Input
                  value={novaEspecie.nome}
                  onChange={(e) => setNovaEspecie({...novaEspecie, nome: e.target.value})}
                  placeholder="Ex: APOSENTADORIA POR IDADE"
                />
              </div>
            </div>
            <div>
              <Label>Tipo *</Label>
              <Select value={novaEspecie.tipo} onValueChange={(v) => setNovaEspecie({...novaEspecie, tipo: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="APOSENTADORIA">Aposentadoria</SelectItem>
                  <SelectItem value="PENSAO">Pensão</SelectItem>
                  <SelectItem value="AUXILIO">Auxílio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={novaEspecie.ativo}
                onCheckedChange={(v) => setNovaEspecie({...novaEspecie, ativo: v})}
              />
              <Label>Espécie Ativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNovaEspecie(false); setEspecieEditando(null); }}>Cancelar</Button>
            <Button onClick={salvarEspecie} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {especieEditando ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Importação */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Importar Órgãos
            </DialogTitle>
            <DialogDescription>
              Importe órgãos a partir de um arquivo Excel (.xlsx)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                className="hidden"
                id="import-file"
              />
              <label 
                htmlFor="import-file"
                className="cursor-pointer"
              >
                {importFile ? (
                  <div className="space-y-2">
                    <FileText className="h-10 w-10 mx-auto text-green-600" />
                    <p className="font-medium text-green-600">{importFile.name}</p>
                    <p className="text-sm text-gray-500">Clique para trocar o arquivo</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-10 w-10 mx-auto text-gray-400" />
                    <p className="font-medium text-gray-700">Clique para selecionar o arquivo</p>
                    <p className="text-sm text-gray-500">Formatos: .xlsx, .xls, .csv</p>
                  </div>
                )}
              </label>
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
              <p className="font-medium mb-1">Formato esperado do arquivo:</p>
              <ul className="list-disc list-inside space-y-0.5 text-blue-700">
                <li>Coluna "Código" (obrigatório)</li>
                <li>Coluna "Nome" (obrigatório)</li>
                <li>Coluna "Tipo" (FEDERAL, ESTADUAL, MUNICIPAL, INSS, MILITAR, CLT_EMPRESA_PRIVADA)</li>
                <li>Coluna "UF" (sigla do estado ou TODOS)</li>
                <li>Colunas opcionais: Convênio, Margem Consignável, Margem Cartão</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowImportModal(false); setImportFile(null); }}>
              Cancelar
            </Button>
            <Button onClick={handleImportFile} disabled={importing || !importFile} className="bg-blue-600 hover:bg-blue-700">
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Importar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
